'use client';

import { Button } from '~/components/ui/button';
import { Form } from '../ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { trpc } from '~/trpc/client';
import { FormTextArea } from '../forms/form-textarea';
import {
  type NpsFeedbackFormSchema,
  npsFeedbackFormSchema,
} from '~/validation-schemas/nps-feedback-form';
import { toast } from 'sonner';
import { isTRPCClientError } from '~/trpc/is-trpc-error';
import { cn } from '~/lib/utils';
import { type CallToActionButtonStyleReturn } from './call-to-action';
import { Checkbox } from '../ui/checkbox';
import { FormRadioGroup } from '../forms/form-radio-group';

interface BlockNpsFeedbackFormProps {
  className?: string;
  customButton: CallToActionButtonStyleReturn;
  formIdentifier: string;
  pageUrl: string;
}

export function BlockNpsFeedbackForm({
  className,
  customButton,
  formIdentifier,
  pageUrl,
}: Readonly<BlockNpsFeedbackFormProps>) {
  const submitNpsFeedbackForm =
    trpc.formRouter.submitNpsFeedbackForm.useMutation();

  const form = useForm<NpsFeedbackFormSchema>({
    resolver: zodResolver(npsFeedbackFormSchema),
    defaultValues: {
      likelyToRecommendRating: undefined,
      likelyToRecommendReasons: '',
      overallExperienceRating: undefined,
      overallExperienceSuggestions: '',
      easeOfDoingBusinessRating: undefined,
      easeOfDoingBusinessSuggestions: '',
      brandOfChoiceRating: undefined,
      brandOfChoiceReasons: '',
      useInMarketing: false,
    },
  });

  const onSubmit = async (data: NpsFeedbackFormSchema) => {
    if (submitNpsFeedbackForm.isPending) return;
    try {
      await toast
        .promise(
          submitNpsFeedbackForm.mutateAsync({
            ...data,
            formIdentifier,
            pageUrl,
          }),
          {
            loading: 'Submitting NPS feedback...',
            success: () => {
              return `NPS feedback submitted successfully`;
            },
            error: (error) => {
              if (isTRPCClientError(error)) {
                return error.message;
              }
              return `Error submitting NPS feedback`;
            },
          },
        )
        .unwrap();
      form.reset();
    } catch (error) {
      console.error('Error submitting NPS feedback:', error);
    }
  };

  const isDisabled = form.formState.isSubmitting;

  return (
    <div className={cn('w-full sm:mx-auto sm:max-w-4xl', className)}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="space-y-6 px-2">
            <div>
              <h2 className="mb-2 text-lg font-semibold">
                How would you rate your overall experience?
              </h2>
              <div className="flex gap-2 lg:w-max lg:max-w-full lg:flex-col">
                <div className="text-s flex flex-col justify-between whitespace-nowrap font-semibold text-muted-foreground lg:flex-row">
                  <span>Very Poor</span>
                  <span>Excellent</span>
                </div>
                <FormRadioGroup
                  key={String(form.watch('overallExperienceRating'))}
                  name="overallExperienceRating"
                  control={form.control}
                  options={[
                    { label: '1', value: '1' },
                    { label: '2', value: '2' },
                    { label: '3', value: '3' },
                    { label: '4', value: '4' },
                    { label: '5', value: '5' },
                  ]}
                  disabled={isDisabled}
                />
              </div>
            </div>
            <div>
              <FormTextArea
                id="overallExperienceSuggestions"
                name="overallExperienceSuggestions"
                label="How can we improve your overall experience? (Optional)"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your suggestions"
                rows={3}
              />
            </div>
            <div>
              <h2 className="mb-2 text-lg font-semibold">
                How easy was it to do business with us?
              </h2>
              <div className="w-full">
                <FormRadioGroup
                  key={String(form.watch('easeOfDoingBusinessRating'))}
                  name="easeOfDoingBusinessRating"
                  control={form.control}
                  options={[
                    {
                      label: 'Extremely Difficult',
                      value: 'Extremely Difficult',
                    },
                    { label: 'Very Difficult', value: 'Very Difficult' },
                    { label: 'Fairly Difficult', value: 'Fairly Difficult' },
                    {
                      label: 'Neither Easy or Difficult',
                      value: 'Neither Easy or Difficult',
                    },
                    { label: 'Fairly Easy', value: 'Fairly Easy' },
                    { label: 'Very Easy', value: 'Very Easy' },
                    { label: 'Extremely Easy', value: 'Extremely Easy' },
                  ]}
                  disabled={isDisabled}
                  rowOnLg={false}
                />
              </div>
            </div>
            <div>
              <FormTextArea
                id="easeOfDoingBusinessSuggestions"
                name="easeOfDoingBusinessSuggestions"
                label="How can we make it easier to do business with us? (Optional)"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your suggestions"
                rows={3}
              />
            </div>
            <div>
              <h2 className="mb-2 text-lg font-semibold">
                How likely are you to recommend us to a friend or colleague if
                they have the same purchasing needs?
              </h2>
              <div className="flex gap-2 lg:w-max lg:max-w-full lg:flex-col">
                <div className="text-s flex flex-col justify-between whitespace-nowrap font-semibold text-muted-foreground lg:flex-row">
                  <span>Not at all likely</span>
                  <span>Extremely likely</span>
                </div>
                <FormRadioGroup
                  key={String(form.watch('likelyToRecommendRating'))}
                  name="likelyToRecommendRating"
                  control={form.control}
                  options={Array.from({ length: 11 }, (_, i) => ({
                    label: i.toString(),
                    value: i.toString(),
                  }))}
                  disabled={isDisabled}
                />
              </div>
            </div>
            <div>
              <FormTextArea
                id="likelyToRecommendReasons"
                name="likelyToRecommendReasons"
                label="Please tell us why you gave this score (Optional)"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your reasons"
                rows={3}
              />
            </div>
            <div>
              <h2 className="mb-2 text-lg font-semibold">
                Thinking about your overall experience with Findel, to what
                extent do you agree with the following statement: &quot;Findel
                is my preferred partner of choice for education resources?&quot;
              </h2>
              <div className="flex gap-2 lg:w-max lg:max-w-full lg:flex-col">
                <div className="text-s flex flex-col justify-between whitespace-nowrap font-semibold text-muted-foreground lg:flex-row">
                  <span>Strongly Disagree</span>
                  <span>Strongly Agree</span>
                </div>
                <FormRadioGroup
                  key={String(form.watch('brandOfChoiceRating'))}
                  name="brandOfChoiceRating"
                  control={form.control}
                  options={[
                    { label: '1', value: '1' },
                    { label: '2', value: '2' },
                    { label: '3', value: '3' },
                    { label: '4', value: '4' },
                    { label: '5', value: '5' },
                  ]}
                  disabled={isDisabled}
                />
              </div>
            </div>
            <div>
              <FormTextArea
                id="brandOfChoiceReasons"
                name="brandOfChoiceReasons"
                label="What makes us your brand of choice? (Optional)"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your reasons"
                rows={3}
              />
            </div>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="useInMarketing"
                  checked={form.watch('useInMarketing')}
                  onCheckedChange={(checked) => {
                    form.setValue('useInMarketing', checked === true);
                  }}
                  disabled={isDisabled}
                />
                <label
                  htmlFor="useInMarketing"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I consent to my feedback being used for marketing purposes
                </label>
              </div>
            </div>
            <div className="pt-4">
              <Button
                type="submit"
                disabled={isDisabled}
                style={customButton?.customButtonStyle}
                className={cn(
                  'mx-auto block w-full sm:w-1/2',
                  customButton?.customButtonClasses,
                )}
                variant={customButton ? 'custom' : 'default'}
              >
                Submit NPS Feedback
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
